import type {
  ClientOrchestrationCommand,
  RelayCapability,
  RelayDeliveryClass,
  RelayJournalPersistence,
  RelayMethodPolicy,
  RelayProtocolErrorCode,
  RelayActionGrantCommandType,
  RelayScope,
} from "@cmmd-forge/contracts";
import { ORCHESTRATION_WS_METHODS, WS_METHODS, WsRpcGroup } from "@cmmd-forge/contracts";
import type * as RpcGroup from "effect/unstable/rpc/RpcGroup";

export class RelayProtocolFailure extends Error {
  override readonly name = "RelayProtocolFailure";

  constructor(
    readonly code: RelayProtocolErrorCode,
    message: string,
    readonly retryable: boolean,
  ) {
    super(message);
  }
}

function canonicalJsonValue(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(canonicalJsonValue);
  if (value && typeof value === "object") {
    // `.sort()` on the fresh array `Object.entries` returns, not `.toSorted()`:
    // Hermes does not ship the ES2023 method, and this runs on the phone for
    // every relay command frame. Reads never hash their payload, so the throw
    // only reached commands, and it surfaced as the socket closing mid-send.
    return Object.fromEntries(
      Object.entries(value as Readonly<Record<string, unknown>>)
        // oxlint-disable-next-line unicorn/no-array-sort
        .sort(([left], [right]) => left.localeCompare(right))
        .map(([key, entry]) => [key, canonicalJsonValue(entry)]),
    );
  }
  return value;
}

export function canonicalRelayPayloadJson(payload: unknown): string {
  const encoded = JSON.stringify(canonicalJsonValue(payload));
  if (encoded === undefined) throw new Error("Relay payload is not JSON serializable.");
  return encoded;
}

export interface RelayTransportControlRequestIdentity {
  readonly requestId: string;
  readonly correlationId: string;
  readonly method: string;
  readonly requiredScope: null;
  readonly deliveryClass: "read";
  readonly journalPersistence: "none";
}

const EFFECT_RPC_PING_IDENTITY = {
  requestId: "@effect/rpc/Ping",
  correlationId: "@effect/rpc/Ping",
  method: "@effect/rpc/Ping",
  requiredScope: null,
  deliveryClass: "read",
  journalPersistence: "none",
} as const satisfies RelayTransportControlRequestIdentity;

export function relayTransportControlRequestIdentity(
  frame: Readonly<Record<string, unknown>>,
): RelayTransportControlRequestIdentity | null {
  return frame._tag === "Ping" ? EFFECT_RPC_PING_IDENTITY : null;
}

export function relayTransportControlResponseRequestId(
  frame: Readonly<Record<string, unknown>>,
): string | null {
  return frame._tag === "Pong" ? EFFECT_RPC_PING_IDENTITY.requestId : null;
}

export interface RelayNegotiationPeer {
  readonly minVersion: number;
  readonly maxVersion: number;
  readonly supportedCapabilities: ReadonlyArray<RelayCapability>;
  readonly requiredCapabilities: ReadonlyArray<RelayCapability>;
}

export interface RelayNegotiationResult {
  readonly protocolVersion: number;
}

function assertRequiredCapabilities(
  required: ReadonlyArray<RelayCapability>,
  supported: ReadonlyArray<RelayCapability>,
): void {
  const supportedSet = new Set(supported);
  const missing = required.find((capability) => !supportedSet.has(capability));
  if (missing !== undefined) {
    throw new RelayProtocolFailure(
      "required_capability_missing",
      `Required relay capability is unavailable: ${missing}`,
      false,
    );
  }
}

export function negotiateRelayProtocol(
  control: RelayNegotiationPeer,
  runtime: RelayNegotiationPeer,
): RelayNegotiationResult {
  const minVersion = Math.max(control.minVersion, runtime.minVersion);
  const maxVersion = Math.min(control.maxVersion, runtime.maxVersion);
  if (minVersion > maxVersion) {
    throw new RelayProtocolFailure(
      "protocol_version_mismatch",
      "Control surface and runtime do not share a relay protocol version",
      false,
    );
  }

  assertRequiredCapabilities(control.requiredCapabilities, runtime.supportedCapabilities);
  assertRequiredCapabilities(runtime.requiredCapabilities, control.supportedCapabilities);

  return { protocolVersion: maxVersion };
}

export function validateRelayEpoch(input: {
  readonly envelopeEpoch: number;
  readonly activeEpoch: number;
}): void {
  if (input.envelopeEpoch !== input.activeEpoch) {
    throw new RelayProtocolFailure(
      "stale_connection_epoch",
      `Relay envelope epoch ${input.envelopeEpoch} does not match active epoch ${input.activeEpoch}`,
      true,
    );
  }
}

export function authorizeRelayRequest(input: {
  readonly method: string;
  readonly grantedScopes: ReadonlyArray<RelayScope>;
  readonly policy: RelayMethodPolicy | null;
}): {
  readonly requiredScope: RelayScope | null;
  readonly deliveryClass: RelayDeliveryClass;
  readonly journalPersistence: RelayJournalPersistence;
} {
  if (input.policy === null || input.policy.method !== input.method) {
    throw new RelayProtocolFailure(
      "method_not_remotable",
      `Relay method has no exact dispatch policy: ${input.method}`,
      false,
    );
  }

  if (
    input.policy.requiredScope !== null &&
    !input.grantedScopes.includes(input.policy.requiredScope)
  ) {
    throw new RelayProtocolFailure(
      "scope_denied",
      `Relay method ${input.method} requires scope ${input.policy.requiredScope}`,
      false,
    );
  }

  return {
    requiredScope: input.policy.requiredScope,
    deliveryClass: input.policy.deliveryClass,
    journalPersistence: input.policy.journalPersistence,
  };
}

export function validateRelayMethodPolicies(input: {
  readonly registeredMethods: ReadonlyArray<string>;
  readonly policies: ReadonlyArray<RelayMethodPolicy>;
}): ReadonlyMap<string, RelayMethodPolicy> {
  const registeredMethods = new Set(input.registeredMethods);
  const policyByMethod = new Map<string, RelayMethodPolicy>();
  for (const policy of input.policies) {
    if (policyByMethod.has(policy.method)) {
      throw new RelayProtocolFailure(
        "method_not_remotable",
        `Relay method has duplicate dispatch policies: ${policy.method}`,
        false,
      );
    }
    if (!registeredMethods.has(policy.method)) {
      throw new RelayProtocolFailure(
        "method_not_remotable",
        `Relay dispatch policy targets an unregistered method: ${policy.method}`,
        false,
      );
    }
    const durable =
      policy.deliveryClass === "idempotent-command" || policy.deliveryClass === "create-command";
    if (durable === (policy.journalPersistence === "none")) {
      throw new RelayProtocolFailure(
        "method_not_remotable",
        `Relay dispatch policy has incompatible journal persistence: ${policy.method}`,
        false,
      );
    }
    policyByMethod.set(policy.method, policy);
  }

  for (const method of input.registeredMethods) {
    if (!policyByMethod.has(method)) {
      throw new RelayProtocolFailure(
        "method_not_remotable",
        `Remotely registered method has no dispatch policy: ${method}`,
        false,
      );
    }
  }
  return policyByMethod;
}

type RelayMethodPolicyBase = Omit<RelayMethodPolicy, "journalPersistence">;

const RELAY_METHOD_POLICY_BASES = [
  {
    method: WS_METHODS.projectsSearchContents,
    requiredScope: "workspace.read",
    deliveryClass: "read",
  },
  {
    method: WS_METHODS.projectsSearchEntries,
    requiredScope: "workspace.read",
    deliveryClass: "read",
  },
  // The same workspace index, listed rather than searched, for the mobile Files
  // screen. Same scope and class as its search counterpart: a read of the tree.
  {
    method: WS_METHODS.projectsListEntries,
    requiredScope: "workspace.read",
    deliveryClass: "read",
  },
  // Reports which VCS and provider tooling this host has. Reads the machine, not
  // the workspace, and changes nothing.
  {
    method: WS_METHODS.serverDiscoverSourceControl,
    requiredScope: "runtime.read",
    deliveryClass: "read",
  },
  // A client telling the server how it is doing. Forge records nothing and
  // answers nothing, so it needs no more authority than a read.
  {
    method: WS_METHODS.serverReportClientActivity,
    requiredScope: "runtime.read",
    deliveryClass: "read",
  },
  {
    method: WS_METHODS.projectsWriteFile,
    requiredScope: "workspace.write",
    deliveryClass: "idempotent-command",
  },
  {
    method: WS_METHODS.projectCheckoutDeletionPreview,
    requiredScope: "workspace.write",
    deliveryClass: "read",
  },
  {
    method: WS_METHODS.projectCheckoutDeletionExecute,
    requiredScope: "workspace.write",
    deliveryClass: "idempotent-command",
  },
  {
    /**
     * The git half of a review. `diff.read` rather than `workspace.read`,
     * because that scope already exists for exactly this: reading changes
     * rather than reading the tree.
     */
    method: WS_METHODS.reviewGetDiffPreview,
    requiredScope: "diff.read",
    deliveryClass: "read",
  },
  {
    /**
     * Reading one workspace file for the viewer. `workspace.read` like its
     * neighbours, and unlike them it is confined to the project root by two
     * checks, the second of which runs after symlinks are resolved.
     */
    method: WS_METHODS.projectsReadFile,
    requiredScope: "workspace.read",
    deliveryClass: "read",
  },
  {
    /**
     * Listing the caller's cloud runtimes. `runtime.read` because that is what
     * this is: a read of runtime inventory. The answer is scoped by CMMD from
     * the caller's own JWT, so this grants no reach beyond what that token
     * already carries.
     */
    method: WS_METHODS.cloudListRuntimes,
    requiredScope: "runtime.read",
    deliveryClass: "read",
  },
  {
    /**
     * Listing directories so a person can pick a project folder.
     *
     * `workspace.read` rather than `project.create`, because the act is a read
     * of the host and the project may not exist yet. It grants no reach a
     * client with `terminal.write` did not already have, and less than
     * `workspaceReadFile` beside it, which returns contents rather than names.
     */
    method: WS_METHODS.filesystemBrowse,
    requiredScope: "workspace.read",
    deliveryClass: "read",
  },
  {
    /**
     * One workspace directory's immediate children, for the Files tree. A read
     * of names and directory shapes, `workspace.read` like the neighbouring
     * tree reads; the server confines the listed path to the workspace root.
     */
    method: WS_METHODS.workspaceListDirectory,
    requiredScope: "workspace.read",
    deliveryClass: "read",
  },
  {
    method: WS_METHODS.workspaceReadFile,
    requiredScope: "workspace.read",
    deliveryClass: "bulk-transfer",
  },
  {
    method: WS_METHODS.workspaceBeginWriteFile,
    requiredScope: "workspace.write",
    deliveryClass: "idempotent-command",
  },
  {
    method: WS_METHODS.workspaceWriteFileChunk,
    requiredScope: "workspace.write",
    deliveryClass: "bulk-transfer",
  },
  {
    method: WS_METHODS.workspaceCommitWriteFile,
    requiredScope: "workspace.write",
    deliveryClass: "idempotent-command",
  },
  {
    method: WS_METHODS.workspaceCancelWriteFile,
    requiredScope: "workspace.write",
    deliveryClass: "idempotent-command",
  },
  {
    method: WS_METHODS.shellOpenInEditor,
    requiredScope: "shell.open",
    deliveryClass: "idempotent-command",
  },
  { method: WS_METHODS.gitPull, requiredScope: "git.write", deliveryClass: "idempotent-command" },
  { method: WS_METHODS.gitRefreshStatus, requiredScope: "git.read", deliveryClass: "read" },
  {
    method: WS_METHODS.gitRunStackedAction,
    requiredScope: "git.write",
    deliveryClass: "idempotent-command",
  },
  { method: WS_METHODS.gitListBranches, requiredScope: "git.read", deliveryClass: "read" },
  // Same operation under t3code's vocabulary (see contracts/src/vcsCompat.ts), so
  // it carries the same scope and delivery class as the method it delegates to.
  // Diverging here would let a client reach the same data through a weaker gate.
  { method: WS_METHODS.vcsListRefs, requiredScope: "git.read", deliveryClass: "read" },
  { method: WS_METHODS.vcsRefreshStatus, requiredScope: "git.read", deliveryClass: "read" },
  { method: WS_METHODS.vcsPull, requiredScope: "git.write", deliveryClass: "idempotent-command" },
  {
    method: WS_METHODS.vcsCreateWorktree,
    requiredScope: "git.write",
    deliveryClass: "create-command",
  },
  {
    method: WS_METHODS.vcsRemoveWorktree,
    requiredScope: "git.write",
    deliveryClass: "idempotent-command",
  },
  { method: WS_METHODS.vcsCreateRef, requiredScope: "git.write", deliveryClass: "create-command" },
  {
    method: WS_METHODS.vcsSwitchRef,
    requiredScope: "git.write",
    deliveryClass: "idempotent-command",
  },
  { method: WS_METHODS.vcsInit, requiredScope: "git.write", deliveryClass: "idempotent-command" },
  {
    method: WS_METHODS.gitCreateWorktree,
    requiredScope: "git.write",
    deliveryClass: "create-command",
  },
  {
    method: WS_METHODS.gitRemoveWorktree,
    requiredScope: "git.write",
    deliveryClass: "idempotent-command",
  },
  {
    method: WS_METHODS.gitCreateBranch,
    requiredScope: "git.write",
    deliveryClass: "create-command",
  },
  {
    method: WS_METHODS.gitCheckout,
    requiredScope: "git.write",
    deliveryClass: "idempotent-command",
  },
  { method: WS_METHODS.gitInit, requiredScope: "git.write", deliveryClass: "idempotent-command" },
  { method: WS_METHODS.gitResolvePullRequest, requiredScope: "git.read", deliveryClass: "read" },
  {
    method: WS_METHODS.gitPreparePullRequestThread,
    requiredScope: "git.write",
    deliveryClass: "idempotent-command",
  },
  {
    method: WS_METHODS.terminalOpen,
    requiredScope: "terminal.write",
    deliveryClass: "create-command",
  },
  {
    // Streams a session, but may also spawn the pty backing it, so it is gated on
    // terminal.write rather than terminal.read. Classed as terminal-output because
    // what flows back is a session's output, and that is what shapes its delivery.
    method: WS_METHODS.terminalAttach,
    requiredScope: "terminal.write",
    deliveryClass: "terminal-output",
  },
  {
    method: WS_METHODS.terminalWrite,
    requiredScope: "terminal.write",
    deliveryClass: "idempotent-command",
  },
  {
    method: WS_METHODS.terminalResize,
    requiredScope: "terminal.write",
    deliveryClass: "idempotent-command",
  },
  {
    method: WS_METHODS.terminalSignal,
    requiredScope: "terminal.write",
    deliveryClass: "idempotent-command",
  },
  {
    method: WS_METHODS.terminalClear,
    requiredScope: "terminal.write",
    deliveryClass: "idempotent-command",
  },
  {
    method: WS_METHODS.terminalRestart,
    requiredScope: "terminal.write",
    deliveryClass: "idempotent-command",
  },
  {
    method: WS_METHODS.terminalClose,
    requiredScope: "terminal.write",
    deliveryClass: "idempotent-command",
  },
  {
    method: WS_METHODS.browserOpenSession,
    requiredScope: "browser.control",
    deliveryClass: "create-command",
  },
  { method: WS_METHODS.browserListSessions, requiredScope: "browser.read", deliveryClass: "read" },
  {
    method: WS_METHODS.browserCloseSession,
    requiredScope: "browser.control",
    deliveryClass: "browser-control",
  },
  { method: WS_METHODS.browserGetSession, requiredScope: "browser.read", deliveryClass: "read" },
  {
    method: WS_METHODS.browserCommand,
    requiredScope: "browser.control",
    deliveryClass: "browser-control",
  },
  {
    method: WS_METHODS.browserClearBrowsingData,
    requiredScope: "browser.control",
    deliveryClass: "browser-control",
  },
  {
    method: WS_METHODS.browserAttachDisplay,
    requiredScope: "browser.read",
    deliveryClass: "subscription",
  },
  {
    // RATIFIED (CMMD-2237): `computer.input` rides `browser.control` rather than
    // a minted `display:control`. Same scope as `browser.command` because both
    // let the caller act on the runtime the user is watching, and read scope is
    // not enough to move a mouse.
    //
    // Why not a distinct scope: RELAY_SCOPES is generated from CMMD's scope list,
    // so minting one is a cross-repo change, and the finer grain would not buy a
    // real boundary — anything holding `browser.control` can already drive the
    // page through CDP on this same display.
    //
    // The cost this accepts, recorded so it is re-examinable rather than
    // forgotten: a token scoped for browser automation also gets raw X input over
    // the whole desktop, which is strictly wider than the page. Revisit if
    // `browser.control` is ever handed to something that should drive a page but
    // not the machine. `ComputerInputRpc.test.ts` pins the two scopes equal so
    // narrowing one without the other fails loudly.
    method: WS_METHODS.computerInput,
    requiredScope: "browser.control",
    deliveryClass: "browser-control",
  },
  {
    method: WS_METHODS.browserAddDisplayIceCandidate,
    requiredScope: "browser.read",
    deliveryClass: "browser-control",
  },
  {
    method: WS_METHODS.devTunnelRegister,
    requiredScope: "browser.control",
    deliveryClass: "idempotent-command",
  },
  {
    method: WS_METHODS.devTunnelUnregister,
    requiredScope: "browser.control",
    deliveryClass: "idempotent-command",
  },
  { method: WS_METHODS.devTunnelList, requiredScope: "browser.read", deliveryClass: "read" },
  { method: WS_METHODS.threadDevRuntimeGet, requiredScope: "runtime.read", deliveryClass: "read" },
  {
    method: WS_METHODS.threadDevRuntimeAllocatePort,
    requiredScope: "runtime.manage",
    deliveryClass: "idempotent-command",
  },
  {
    method: WS_METHODS.threadDevRuntimeRelease,
    requiredScope: "runtime.manage",
    deliveryClass: "idempotent-command",
  },
  {
    method: WS_METHODS.workspaceEnvironmentPreflight,
    requiredScope: "workspace.read",
    deliveryClass: "read",
  },
  {
    // Creates a directory, so it needs write scope rather than `workspace.read`
    // like its preflight sibling. Idempotent by construction — the path is
    // derived and `mkdir` is recursive — so a redelivered call is a no-op
    // rather than a second scratch workspace.
    method: WS_METHODS.workspaceEnsureScratch,
    requiredScope: "workspace.write",
    deliveryClass: "idempotent-command",
  },
  { method: WS_METHODS.serverGetConfig, requiredScope: "runtime.read", deliveryClass: "read" },
  {
    method: WS_METHODS.serverRefreshProviders,
    requiredScope: "provider.read",
    deliveryClass: "read",
  },
  {
    method: WS_METHODS.serverStartProviderAuth,
    requiredScope: "provider.manage",
    deliveryClass: "create-command",
  },
  {
    method: WS_METHODS.serverRepairProviderAuthRuntime,
    requiredScope: "provider.manage",
    deliveryClass: "idempotent-command",
  },
  {
    method: WS_METHODS.serverDisconnectProviderAuth,
    requiredScope: "provider.manage",
    deliveryClass: "idempotent-command",
  },
  {
    method: WS_METHODS.serverGetProviderAuthSession,
    requiredScope: "provider.read",
    deliveryClass: "read",
  },
  {
    method: WS_METHODS.serverCancelProviderAuth,
    requiredScope: "provider.manage",
    deliveryClass: "idempotent-command",
  },
  {
    method: WS_METHODS.serverSubmitProviderAuthCode,
    requiredScope: "provider.manage",
    deliveryClass: "idempotent-command",
  },
  {
    method: WS_METHODS.serverSyncPersonalCodexToCloud,
    requiredScope: "provider.manage",
    deliveryClass: "idempotent-command",
  },
  {
    method: WS_METHODS.serverSyncPersonalClaudeToCloud,
    requiredScope: "provider.manage",
    deliveryClass: "idempotent-command",
  },
  {
    method: WS_METHODS.serverUpsertKeybinding,
    requiredScope: "settings.write",
    deliveryClass: "idempotent-command",
  },
  {
    method: WS_METHODS.serverRemoveKeybinding,
    requiredScope: "settings.write",
    deliveryClass: "idempotent-command",
  },
  { method: WS_METHODS.serverGetSettings, requiredScope: "settings.read", deliveryClass: "read" },
  {
    method: WS_METHODS.serverUpdateSettings,
    requiredScope: "settings.write",
    deliveryClass: "idempotent-command",
  },
  {
    method: WS_METHODS.serverUpdateEnvironmentDisplayName,
    requiredScope: "settings.write",
    deliveryClass: "idempotent-command",
  },
  // A read of server-local observability state, so it rides beside
  // `serverGetSettings` rather than the runtime scopes: what it returns is the
  // server's own configuration and health, not a runtime's.
  {
    method: WS_METHODS.serverGetTraceDiagnostics,
    requiredScope: "settings.read",
    deliveryClass: "read",
  },
  { method: WS_METHODS.serverHeartbeat, requiredScope: "runtime.read", deliveryClass: "read" },
  // Reading what a runtime has spent is a read of that runtime's own state, so
  // it rides the scope every client already needs to reach it at all.
  { method: WS_METHODS.usageGetSummary, requiredScope: "runtime.read", deliveryClass: "read" },
  { method: WS_METHODS.pullRequestsList, requiredScope: "git.read", deliveryClass: "read" },
  { method: WS_METHODS.pullRequestsListStats, requiredScope: "git.read", deliveryClass: "read" },
  { method: WS_METHODS.pullRequestsDetail, requiredScope: "git.read", deliveryClass: "read" },
  { method: WS_METHODS.pullRequestsActivity, requiredScope: "git.read", deliveryClass: "read" },
  {
    method: WS_METHODS.pullRequestsThreadComments,
    requiredScope: "git.read",
    deliveryClass: "read",
  },
  {
    method: WS_METHODS.pullRequestsDiffFileContents,
    requiredScope: "git.read",
    deliveryClass: "read",
  },
  {
    method: WS_METHODS.pullRequestsRunAction,
    requiredScope: "git.write",
    deliveryClass: "idempotent-command",
  },
  {
    method: WS_METHODS.pullRequestsUpdate,
    requiredScope: "git.write",
    deliveryClass: "idempotent-command",
  },
  {
    method: WS_METHODS.pullRequestsComment,
    requiredScope: "git.write",
    deliveryClass: "create-command",
  },
  {
    method: WS_METHODS.pullRequestsUpdateComment,
    requiredScope: "git.write",
    deliveryClass: "idempotent-command",
  },
  {
    method: WS_METHODS.pullRequestsSubmitReview,
    requiredScope: "git.write",
    deliveryClass: "create-command",
  },
  {
    method: WS_METHODS.pullRequestsReplyToThread,
    requiredScope: "git.write",
    deliveryClass: "create-command",
  },
  {
    method: WS_METHODS.pullRequestsSetThreadResolution,
    requiredScope: "git.write",
    deliveryClass: "idempotent-command",
  },
  {
    method: WS_METHODS.pullRequestsSetReaction,
    requiredScope: "git.write",
    deliveryClass: "idempotent-command",
  },
  { method: WS_METHODS.pullRequestsInvalidate, requiredScope: "git.read", deliveryClass: "read" },
  {
    method: WS_METHODS.pullRequestsReviewerCandidates,
    requiredScope: "git.read",
    deliveryClass: "read",
  },
  {
    method: WS_METHODS.pullRequestsRequestReviewers,
    requiredScope: "git.write",
    deliveryClass: "idempotent-command",
  },
  {
    method: WS_METHODS.subscribeGitStatus,
    requiredScope: "git.read",
    deliveryClass: "subscription",
  },
  {
    method: WS_METHODS.subscribeVcsStatus,
    requiredScope: "git.read",
    deliveryClass: "subscription",
  },
  {
    method: WS_METHODS.subscribeTerminalEvents,
    requiredScope: "terminal.read",
    deliveryClass: "terminal-output",
  },
  // The same terminals, as a list feed rather than an output stream: it carries
  // session metadata only, so it reads rather than streams output.
  {
    method: WS_METHODS.subscribeTerminalMetadata,
    requiredScope: "terminal.read",
    deliveryClass: "read",
  },
  {
    method: WS_METHODS.subscribeBrowserEvents,
    requiredScope: "browser.read",
    deliveryClass: "browser-frame",
  },
  {
    method: WS_METHODS.subscribeServerConfig,
    requiredScope: "runtime.read",
    deliveryClass: "subscription",
  },
  {
    method: WS_METHODS.subscribeServerLifecycle,
    requiredScope: "runtime.read",
    deliveryClass: "subscription",
  },
  {
    method: WS_METHODS.subscribeAuthAccess,
    requiredScope: "provider.read",
    deliveryClass: "subscription",
  },
  {
    method: WS_METHODS.subscribeWorkspaceReadiness,
    requiredScope: "runtime.read",
    deliveryClass: "subscription",
  },
  {
    method: ORCHESTRATION_WS_METHODS.dispatchCommand,
    requiredScope: null,
    deliveryClass: "idempotent-command",
  },
  {
    method: ORCHESTRATION_WS_METHODS.getTurnDiff,
    requiredScope: "diff.read",
    deliveryClass: "read",
  },
  {
    method: ORCHESTRATION_WS_METHODS.getFullThreadDiff,
    requiredScope: "diff.read",
    deliveryClass: "read",
  },
  {
    method: ORCHESTRATION_WS_METHODS.streamTurnDiff,
    requiredScope: "diff.read",
    deliveryClass: "bulk-transfer",
  },
  {
    method: ORCHESTRATION_WS_METHODS.streamFullThreadDiff,
    requiredScope: "diff.read",
    deliveryClass: "bulk-transfer",
  },
  {
    method: ORCHESTRATION_WS_METHODS.replayEvents,
    requiredScope: "thread.read",
    deliveryClass: "subscription",
  },
  {
    method: ORCHESTRATION_WS_METHODS.getArchivedShellSnapshot,
    requiredScope: "thread.read",
    deliveryClass: "read",
  },
  {
    method: ORCHESTRATION_WS_METHODS.subscribeShell,
    requiredScope: "thread.read",
    deliveryClass: "subscription",
  },
  {
    method: ORCHESTRATION_WS_METHODS.subscribeThread,
    requiredScope: "thread.read",
    deliveryClass: "subscription",
  },
  {
    // The relay-only path for "load earlier turns": same read as the thread
    // itself, just windowed to an older page, so it carries the same scope
    // as subscribeThread and the other thread reads above.
    method: ORCHESTRATION_WS_METHODS.getThreadDetailPage,
    requiredScope: "thread.read",
    deliveryClass: "read",
  },
  {
    /**
     * Where to fetch an attachment or a project favicon from.
     *
     * `thread.read` because that is what the bytes belong to, and because the
     * URL this returns carries a credential scoped to exactly the one resource
     * named in the request. It reaches nothing a client that could already read
     * the thread could not read.
     */
    method: WS_METHODS.assetsCreateUrl,
    requiredScope: "thread.read",
    deliveryClass: "read",
  },
  {
    // A pending upload prepares input for an existing turn. A read-only relay
    // grant must not mint bearer URLs, and thread creation is not required.
    method: WS_METHODS.attachmentsCreateUploadUrl,
    requiredScope: "turn.dispatch",
    deliveryClass: "create-command",
  },
  {
    // Deleting a pending upload is idempotent, but still mutates turn input.
    method: WS_METHODS.attachmentsDelete,
    requiredScope: "turn.dispatch",
    deliveryClass: "idempotent-command",
  },
  {
    method: ORCHESTRATION_WS_METHODS.searchThreads,
    requiredScope: "thread.read",
    deliveryClass: "read",
  },
  {
    /**
     * `/feedback` from inside a thread.
     *
     * A create, because each call files a new report; `thread.read` because
     * reading the thread is the whole of the authority it needs, and it writes
     * nothing back into the thread.
     */
    method: WS_METHODS.providerUploadFeedback,
    requiredScope: "thread.read",
    deliveryClass: "create-command",
  },
  {
    method: WS_METHODS.sourceControlLookupRepository,
    requiredScope: "git.read",
    deliveryClass: "read",
  },
  // A clone writes a working tree and produces what becomes a project, so it
  // carries the same scope as `project.create` rather than a repository-read
  // one. A grant that may read repositories but was never given the right to
  // add a project cannot use this to add one.
  {
    method: WS_METHODS.sourceControlCloneRepository,
    requiredScope: "project.write",
    deliveryClass: "idempotent-command",
  },
] as const satisfies ReadonlyArray<RelayMethodPolicyBase>;

const FRAME_JOURNALED_METHODS = new Set<string>([
  WS_METHODS.gitPull,
  WS_METHODS.gitRunStackedAction,
  WS_METHODS.gitCreateWorktree,
  WS_METHODS.gitRemoveWorktree,
  WS_METHODS.gitCreateBranch,
  WS_METHODS.gitCheckout,
  WS_METHODS.gitInit,
  WS_METHODS.gitPreparePullRequestThread,
  WS_METHODS.threadDevRuntimeAllocatePort,
  WS_METHODS.threadDevRuntimeRelease,
  WS_METHODS.serverUpsertKeybinding,
  WS_METHODS.serverRemoveKeybinding,
  WS_METHODS.serverUpdateEnvironmentDisplayName,
  ORCHESTRATION_WS_METHODS.dispatchCommand,
]);

const RELAY_METHOD_POLICIES: ReadonlyArray<RelayMethodPolicy> = RELAY_METHOD_POLICY_BASES.map(
  (policy) => {
    const durable =
      policy.deliveryClass === "idempotent-command" || policy.deliveryClass === "create-command";
    return {
      method: policy.method,
      requiredScope: policy.requiredScope,
      deliveryClass: policy.deliveryClass,
      journalPersistence: durable
        ? FRAME_JOURNALED_METHODS.has(policy.method)
          ? "frame"
          : "metadata-only"
        : "none",
    };
  },
);

const registeredRelayMethods = [...WsRpcGroup.requests.keys()];

export const relayMethodPoliciesByMethod = validateRelayMethodPolicies({
  registeredMethods: registeredRelayMethods,
  policies: RELAY_METHOD_POLICIES,
});

/**
 * Every method the WebSocket RPC group can dispatch, derived from the group
 * itself so the scope map cannot silently fall behind it.
 */
type WsRpcMethod = RpcGroup.Rpcs<typeof WsRpcGroup>["_tag"];

type DeclaredRelayMethods = (typeof RELAY_METHOD_POLICY_BASES)[number]["method"];

/**
 * `validateRelayMethodPolicies` above refuses to start the process when a
 * registered method has no entry in `RELAY_METHOD_POLICY_BASES`, but that
 * refusal only fires where the module is imported. This moves the same
 * totality into the type system, so a checkout that never runs the process
 * still fails `tsc` when an RPC joins the group without a declared scope, or
 * when a policy outlives the method it named. The assigned value is the
 * assertion. The failing branch names whatever broke totality, so the type
 * error lists the offending methods.
 */
const relayScopeMapIsTotal: [Exclude<WsRpcMethod, DeclaredRelayMethods>] extends [never]
  ? [Exclude<DeclaredRelayMethods, WsRpcMethod>] extends [never]
    ? true
    : Exclude<DeclaredRelayMethods, WsRpcMethod>
  : Exclude<WsRpcMethod, DeclaredRelayMethods> = true;

void relayScopeMapIsTotal;

export interface RelayCommandPolicy {
  readonly requiredScope: RelayScope;
  readonly deliveryClass: RelayDeliveryClass;
}

const relayCommandPolicyByType = {
  "project.create": { requiredScope: "project.write", deliveryClass: "create-command" },
  "project.meta.update": { requiredScope: "project.write", deliveryClass: "idempotent-command" },
  "project.script.update": { requiredScope: "project.write", deliveryClass: "idempotent-command" },
  "project.delete": { requiredScope: "project.write", deliveryClass: "idempotent-command" },
  "thread.create": { requiredScope: "thread.create", deliveryClass: "create-command" },
  "thread.delete": { requiredScope: "thread.mutate", deliveryClass: "idempotent-command" },
  "thread.archive": { requiredScope: "thread.mutate", deliveryClass: "idempotent-command" },
  "thread.settle": { requiredScope: "thread.mutate", deliveryClass: "idempotent-command" },
  "thread.unsettle": { requiredScope: "thread.mutate", deliveryClass: "idempotent-command" },
  "thread.unarchive": { requiredScope: "thread.mutate", deliveryClass: "idempotent-command" },
  "thread.meta.update": { requiredScope: "thread.mutate", deliveryClass: "idempotent-command" },
  "thread.runtime-mode.set": {
    requiredScope: "thread.mutate",
    deliveryClass: "idempotent-command",
  },
  "thread.interaction-mode.set": {
    requiredScope: "thread.mutate",
    deliveryClass: "idempotent-command",
  },
  "thread.turn.start": { requiredScope: "turn.dispatch", deliveryClass: "create-command" },
  "thread.turn.interrupt": { requiredScope: "turn.stop", deliveryClass: "idempotent-command" },
  "thread.approval.respond": {
    requiredScope: "approval.respond",
    deliveryClass: "idempotent-command",
  },
  "thread.user-input.respond": {
    requiredScope: "approval.respond",
    deliveryClass: "idempotent-command",
  },
  "thread.checkpoint.revert": {
    requiredScope: "thread.mutate",
    deliveryClass: "idempotent-command",
  },
  "thread.session.stop": { requiredScope: "turn.stop", deliveryClass: "idempotent-command" },
  "thread.plan-approval.respond": {
    requiredScope: "approval.respond",
    deliveryClass: "idempotent-command",
  },
} as const satisfies Record<ClientOrchestrationCommand["type"], RelayCommandPolicy>;

const SENSITIVE_RELAY_ACTION_COMMANDS = new Set<RelayActionGrantCommandType>([
  "thread.approval.respond",
  "thread.user-input.respond",
  "thread.plan-approval.respond",
]);

export interface RelaySensitiveActionIdentity {
  readonly commandType: RelayActionGrantCommandType;
  readonly commandId: string;
  readonly targetId: string;
}

export function relaySensitiveActionIdentity(input: {
  readonly method: string;
  readonly payload: unknown;
}): RelaySensitiveActionIdentity | null {
  if (input.method !== ORCHESTRATION_WS_METHODS.dispatchCommand) return null;
  if (!input.payload || typeof input.payload !== "object" || Array.isArray(input.payload)) {
    return null;
  }
  const command = input.payload as Readonly<Record<string, unknown>>;
  if (
    typeof command.type !== "string" ||
    !SENSITIVE_RELAY_ACTION_COMMANDS.has(command.type as RelayActionGrantCommandType)
  ) {
    return null;
  }
  const targetId =
    command.type === "thread.plan-approval.respond" ? command.promptId : command.requestId;
  if (
    typeof command.commandId !== "string" ||
    command.commandId.length === 0 ||
    typeof targetId !== "string" ||
    targetId.length === 0
  ) {
    throw new RelayProtocolFailure(
      "action_grant_invalid",
      "Relay sensitive action metadata is incomplete",
      false,
    );
  }
  return {
    commandType: command.type as RelayActionGrantCommandType,
    commandId: command.commandId,
    targetId,
  };
}

export function authorizeRelayRpcRequest(input: {
  readonly method: string;
  readonly payload: unknown;
  readonly grantedScopes: ReadonlyArray<RelayScope>;
}): {
  readonly requiredScope: RelayScope | null;
  readonly deliveryClass: RelayDeliveryClass;
  readonly journalPersistence: RelayJournalPersistence;
} {
  const methodPolicy = relayMethodPoliciesByMethod.get(input.method) ?? null;
  const methodResult = authorizeRelayRequest({
    method: input.method,
    grantedScopes: input.grantedScopes,
    policy: methodPolicy,
  });
  if (input.method !== ORCHESTRATION_WS_METHODS.dispatchCommand) return methodResult;
  if (!input.payload || typeof input.payload !== "object" || !("type" in input.payload)) {
    throw new RelayProtocolFailure(
      "method_not_remotable",
      "Relay orchestration dispatch payload has no command policy",
      false,
    );
  }
  const commandType = (input.payload as { readonly type?: unknown }).type;
  if (typeof commandType !== "string" || !(commandType in relayCommandPolicyByType)) {
    throw new RelayProtocolFailure(
      "method_not_remotable",
      `Relay orchestration command has no dispatch policy: ${String(commandType)}`,
      false,
    );
  }
  const commandPolicy = relayCommandPolicyByType[commandType as ClientOrchestrationCommand["type"]];
  if (!input.grantedScopes.includes(commandPolicy.requiredScope)) {
    throw new RelayProtocolFailure(
      "scope_denied",
      `Relay orchestration command ${commandType} requires scope ${commandPolicy.requiredScope}`,
      false,
    );
  }
  return {
    requiredScope: commandPolicy.requiredScope,
    deliveryClass: commandPolicy.deliveryClass,
    journalPersistence: methodResult.journalPersistence,
  };
}

export type RelayCommandRecord<Result> =
  | {
      readonly requestId: string;
      readonly payloadHash: string;
      readonly state: "received";
    }
  | {
      readonly requestId: string;
      readonly payloadHash: string;
      readonly state: "completed";
      readonly result: Result;
    };

export interface RelayCommandStore<Result> {
  readonly get: (requestId: string) => Promise<RelayCommandRecord<Result> | null>;
  readonly insertReceived: (record: RelayCommandRecord<never>) => Promise<boolean>;
  readonly markCompleted: (requestId: string, result: Result) => Promise<void>;
}

function resolveExistingCommand<Result>(
  record: RelayCommandRecord<Result>,
  payloadHash: string,
): { readonly result: Result; readonly source: "cache" } {
  if (record.payloadHash !== payloadHash) {
    throw new RelayProtocolFailure(
      "request_payload_mismatch",
      `Relay request ${record.requestId} was reused with a different payload`,
      false,
    );
  }
  if (record.state === "received") {
    throw new RelayProtocolFailure(
      "request_in_progress",
      `Relay request ${record.requestId} is already in progress`,
      true,
    );
  }
  return { result: record.result, source: "cache" };
}

export async function dispatchIdempotentRelayCommand<Result>(input: {
  readonly requestId: string;
  readonly payloadHash: string;
  readonly store: RelayCommandStore<Result>;
  readonly execute: () => Promise<Result>;
}): Promise<
  | { readonly result: Result; readonly source: "executed" }
  | { readonly result: Result; readonly source: "cache" }
> {
  const existing = await input.store.get(input.requestId);
  if (existing !== null) return resolveExistingCommand(existing, input.payloadHash);

  const inserted = await input.store.insertReceived({
    requestId: input.requestId,
    payloadHash: input.payloadHash,
    state: "received",
  });
  if (!inserted) {
    const raced = await input.store.get(input.requestId);
    if (raced === null) {
      throw new RelayProtocolFailure(
        "request_in_progress",
        `Relay request ${input.requestId} could not establish ownership`,
        true,
      );
    }
    return resolveExistingCommand(raced, input.payloadHash);
  }

  const result = await input.execute();
  await input.store.markCompleted(input.requestId, result);
  return { result, source: "executed" };
}

export function resolveRelayResume(input: {
  readonly requestedCursor: number;
  readonly oldestCursor: number;
  readonly latestCursor: number;
}): { readonly startAfter: number; readonly latestCursor: number } {
  if (input.requestedCursor < input.oldestCursor) {
    throw new RelayProtocolFailure(
      "resume_cursor_expired",
      `Relay cursor ${input.requestedCursor} is older than retained cursor ${input.oldestCursor}`,
      false,
    );
  }
  if (input.requestedCursor > input.latestCursor) {
    throw new RelayProtocolFailure(
      "resume_cursor_ahead",
      `Relay cursor ${input.requestedCursor} is ahead of latest cursor ${input.latestCursor}`,
      false,
    );
  }
  return { startAfter: input.requestedCursor, latestCursor: input.latestCursor };
}
